import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'
import { useTranslation } from 'react-i18next'

interface TelnyxSetting {
  id: number
  setting_key: string
  setting_value: string
  description: string
  is_encrypted: boolean
}

interface ValidationError {
  field: string
  message: string
}

const TelnyxSettings: React.FC = () => {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<TelnyxSetting[]>([])
  const [formData, setFormData] = useState<{ [key: string]: string }>({})
  const [originalFormData, setOriginalFormData] = useState<{ [key: string]: string }>({})
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [errors, setErrors] = useState<ValidationError[]>([])
  const [testingConnection, setTestingConnection] = useState(false)
  const [isEditingApiKey, setIsEditingApiKey] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/settings/admin/telnyx', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch Telnyx settings')
      }

      const data = await response.json()
      setSettings(data.settings || [])

      // Initialize form data
      const initialFormData: { [key: string]: string } = {}
      data.settings?.forEach((setting: TelnyxSetting) => {
        initialFormData[setting.setting_key] = setting.setting_value || ''
      })
      setFormData(initialFormData)
      setOriginalFormData({ ...initialFormData })
      setChangedFields(new Set()) // Clear any previous changes
      setIsEditingApiKey(false) // Reset editing state

    } catch (err) {
      console.error('Error fetching Telnyx settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }))
    
    // Track changed fields
    setChangedFields(prev => {
      const newSet = new Set(prev)
      if (value !== originalFormData[key]) {
        newSet.add(key)
      } else {
        newSet.delete(key) // Remove if value reverted to original
      }
      return newSet
    })
    
    // Clear field-specific errors
    setErrors(prev => prev.filter(error => error.field !== key))
    setError(null)
    setSuccess(null)
  }

  const getFieldError = (field: string): string | undefined => {
    return errors.find(error => error.field === field)?.message
  }

  // Helper function to display API key with first 3 characters visible
  const formatApiKeyDisplay = (apiKey: string | undefined): string => {
    if (!apiKey || apiKey.length === 0) return ''
    if (apiKey.length <= 3) return apiKey
    return `${apiKey.substring(0, 3)}${'•'.repeat(Math.min(apiKey.length - 3, 20))}`
  }

  // Check if API key field should show the partial key or allow editing
  const isApiKeyFieldFocused = isEditingApiKey || formData.api_key !== originalFormData.api_key || changedFields.has('api_key')

  const validateForm = (): boolean => {
    const newErrors: ValidationError[] = []

    // Welcome message validation
    if (formData.welcome_message_en && formData.welcome_message_en.length > 500) {
      newErrors.push({ field: 'welcome_message_en', message: 'English welcome message must be under 500 characters' })
    }

    if (formData.welcome_message_fr && formData.welcome_message_fr.length > 500) {
      newErrors.push({ field: 'welcome_message_fr', message: 'French welcome message must be under 500 characters' })
    }

    // Business hours validation
    if (formData.business_hours_start && formData.business_hours_end) {
      if (formData.business_hours_start >= formData.business_hours_end) {
        newErrors.push({ field: 'business_hours_end', message: 'End time must be after start time' })
      }
    }

    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      setError('Please fix the validation errors below')
      return
    }

    // Only send changed fields
    const changedData: { [key: string]: string } = {}
    changedFields.forEach(key => {
      changedData[key] = formData[key]
    })

    // If no fields were changed, don't make the API call
    if (Object.keys(changedData).length === 0) {
      setSuccess('No changes to save!')
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/settings/admin/telnyx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ settings: changedData })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save settings')
      }

      const fieldCount = Object.keys(changedData).length
      setSuccess(`SMS settings saved successfully! ${fieldCount} field${fieldCount > 1 ? 's' : ''} updated.`)
      
      // Refresh settings to get updated values
      setIsEditingApiKey(false) // Reset editing state
      await fetchSettings()

    } catch (err) {
      console.error('Error saving Telnyx settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const testConnection = async () => {
    if (!formData.api_key?.trim()) {
      setError('Please enter an API Key before testing the connection')
      return
    }

    try {
      setTestingConnection(true)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/settings/admin/telnyx/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ api_key: formData.api_key })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setSuccess('✅ Connection test successful! Telnyx API is working correctly.')
      } else {
        setError(`❌ Connection test failed: ${data.message || 'Unknown error'}`)
      }

    } catch (err) {
      console.error('Error testing Telnyx connection:', err)
      setError(`❌ Connection test failed: ${err instanceof Error ? err.message : 'Network error'}`)
    } finally {
      setTestingConnection(false)
    }
  }

  const testSmsTemplates = async () => {
    setError(null)
    setSuccess(null)

    try {
      const response = await makeApiCall('api/settings/admin/telnyx/test-sms', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status_name: 'In Progress' // Test with a common status name
        })
      })

      const data = await response.json()

      if (data.success) {
        const { debug } = data
        let message = `SMS Templates Test Results:\n`
        message += `• Table exists: ${debug.tableExists ? 'Yes' : 'No'}\n`
        message += `• Templates for "${debug.searchedStatus}": ${debug.foundTemplates?.length || 0}\n`
        message += `• Total templates in database: ${debug.totalTemplates || 0}\n`
        
        if (debug.allAvailableTemplates?.length > 0) {
          message += `\nAvailable templates:\n`
          debug.allAvailableTemplates.forEach((template: any, index: number) => {
            message += `${index + 1}. Status: "${template.status}" (${template.language})\n`
          })
        }

        // Show success message and open console for detailed info
        setSuccess('📋 SMS template test completed! Check console for details.')
        console.log('SMS Template Test Results:', data.debug)
        alert(message) // Also show in alert for immediate feedback
      } else {
        setError(data.message)
      }
    } catch (error) {
      console.error('SMS template test error:', error)
      setError('Failed to test SMS templates')
    }
  }

  const testDealSms = async () => {
    setError(null)
    setSuccess(null)

    try {
      const response = await makeApiCall('api/settings/admin/telnyx/test-deal-sms', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status_name: 'In Progress', // Test with a status that has templates
          customer_id: 1 // Test customer
        })
      })

      const data = await response.json()

      if (data.success) {
        const { debug } = data
        let message = `Deal SMS Test Results:\n\n`
        message += `✅ Telnyx Configured: ${debug.telnyxConfigured ? 'Yes' : 'No'}\n`
        message += `📝 Test Deal: ${debug.testDeal.name} → "${debug.testDeal.status_name}"\n`
        message += `📱 SMS Result: ${debug.smsResult.success ? 'SUCCESS' : 'FAILED'}\n`
        message += `💬 Message: ${debug.smsResult.message}\n`
        
        if (debug.smsResult.sentTo && debug.smsResult.sentTo.length > 0) {
          message += `📞 Sent to: ${debug.smsResult.sentTo.join(', ')}\n`
        }

        setSuccess('📱 Deal SMS test completed! Check console for details.')
        console.log('Deal SMS Test Results:', data.debug)
        alert(message)
      } else {
        setError(data.message)
      }
    } catch (error) {
      console.error('Deal SMS test error:', error)
      setError('Failed to test deal SMS sending')
    }
  }

  const debugSmsTemplates = async () => {
    setError(null)
    setSuccess(null)

    try {
      const response = await makeApiCall('api/settings/admin/telnyx/debug-sms-templates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status_name: 'In Progress' // Test with a status that should have templates
        })
      })

      const data = await response.json()

      if (data.success) {
        const { debug } = data
        let message = `🔧 SMS Template Debug Results:\n\n`
        message += `Step 1 - Table exists: ${debug.step1_tableExists ? '✅ YES' : '❌ NO'}\n`
        message += `Step 2 - Save English: ${debug.step2_saveEnglish.success ? '✅ SUCCESS' : '❌ FAILED'}\n`
        if (debug.step2_saveEnglish.error) message += `  Error: ${debug.step2_saveEnglish.error}\n`
        message += `Step 3 - Save French: ${debug.step3_saveFrench.success ? '✅ SUCCESS' : '❌ FAILED'}\n`
        if (debug.step3_saveFrench.error) message += `  Error: ${debug.step3_saveFrench.error}\n`
        message += `Step 4 - Fetch back: ${debug.step4_fetchBack.success ? '✅ SUCCESS' : '❌ FAILED'}\n`
        message += `  Found templates: ${debug.step4_fetchBack.templates?.length || 0}\n`
        if (debug.step4_fetchBack.error) message += `  Error: ${debug.step4_fetchBack.error}\n`
        message += `Step 5 - Database check: ${debug.step5_checkDatabase.totalCount} total templates\n`

        setSuccess('🔧 Template debugging completed! Check console for details.')
        console.log('SMS Template Debug Results:', data.debug)
        alert(message)
      } else {
        setError(data.message)
      }
    } catch (error) {
      console.error('Template debug error:', error)
      setError('Failed to debug SMS templates')
    }
  }

  const clearBogusTemplates = async () => {
    if (!confirm('Are you sure you want to clear ALL existing SMS templates? This will remove the bogus migration templates.')) {
      return
    }

    setError(null)
    setSuccess(null)

    try {
      const response = await makeApiCall('api/settings/admin/telnyx/clear-templates', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(`✅ Successfully cleared ${data.deletedCount} bogus SMS templates! Now try saving your real templates.`)
      } else {
        setError(data.message)
      }
    } catch (error) {
      console.error('Clear templates error:', error)
      setError('Failed to clear bogus templates')
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            💬 SMS Settings
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Configure SMS messaging preferences, welcome messages, lead notifications, and SMS-based actions. These settings apply system-wide.
          </p>
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              ℹ️ Admin Only: These settings control the SMS messaging behavior for all users in the system.
            </p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-green-700 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Changed Fields Indicator */}
        {changedFields.size > 0 && (
          <div className="mx-6 mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
            <p className="text-orange-700 dark:text-orange-400 text-sm">
              📝 You have {changedFields.size} unsaved change{changedFields.size > 1 ? 's' : ''}: {Array.from(changedFields).join(', ')}
            </p>
          </div>
        )}

        {/* Settings Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* SMS Configuration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              💬 SMS Configuration
            </h3>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled === 'true'}
                onChange={(e) => handleInputChange('enabled', e.target.checked ? 'true' : 'false')}
                className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Telnyx Integration
              </label>
            </div>

            {/* Welcome Messages - Bilingual */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Welcome Messages
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                These messages are sent to customers the first time they receive a status update, based on their language preference.
              </p>

              {/* English Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🇺🇸 Welcome Message (English)
                </label>
                <textarea
                  value={formData.welcome_message_en || ''}
                  onChange={(e) => handleInputChange('welcome_message_en', e.target.value)}
                  placeholder="Enter welcome message sent to English-speaking customers"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                {getFieldError('welcome_message_en') && (
                  <p className="mt-1 text-xs text-red-600">{getFieldError('welcome_message_en')}</p>
                )}
              </div>

              {/* French Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🇫🇷 Welcome Message (French)
                </label>
                <textarea
                  value={formData.welcome_message_fr || ''}
                  onChange={(e) => handleInputChange('welcome_message_fr', e.target.value)}
                  placeholder="Entrez le message d'accueil envoyé aux clients francophones"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                {getFieldError('welcome_message_fr') && (
                  <p className="mt-1 text-xs text-red-600">{getFieldError('welcome_message_fr')}</p>
                )}
              </div>
            </div>

            {/* Business Hours */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Hours Start
                </label>
                <input
                  type="time"
                  value={formData.business_hours_start || ''}
                  onChange={(e) => handleInputChange('business_hours_start', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Hours End
                </label>
                <input
                  type="time"
                  value={formData.business_hours_end || ''}
                  onChange={(e) => handleInputChange('business_hours_end', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    getFieldError('business_hours_end') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {getFieldError('business_hours_end') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('business_hours_end')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone || ''}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="America/Toronto">Toronto</option>
                  <option value="America/Montreal">Montreal</option>
                  <option value="America/Vancouver">Vancouver</option>
                </select>
              </div>
            </div>

            {/* Respect Business Hours */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="respect_business_hours"
                checked={formData.respect_business_hours === 'true'}
                onChange={(e) => handleInputChange('respect_business_hours', e.target.checked ? 'true' : 'false')}
                className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
              />
              <label htmlFor="respect_business_hours" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Queue SMS outside business hours for next morning delivery
              </label>
            </div>
          </div>

          {/* Lead Notification SMS Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              🚨 Lead SMS Notifications
            </h3>
            
            {/* Enable Lead SMS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enable Lead SMS Notifications
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.lead_sms_enabled === 'true'}
                  onChange={(e) => handleInputChange('lead_sms_enabled', e.target.checked ? 'true' : 'false')}
                  className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Send SMS notifications to brokers when new leads are submitted via custom forms
                </span>
              </div>
              {changedFields.has('lead_sms_enabled') && (
                <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
              )}
            </div>
            
            {/* Lead SMS Template English */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Lead SMS Template (English)
              </label>
              <textarea
                value={formData.lead_notification_sms_en || ''}
                onChange={(e) => handleInputChange('lead_notification_sms_en', e.target.value)}
                placeholder="SMS template sent to brokers for new leads (English)"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                  getFieldError('lead_notification_sms_en') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Available placeholders: (Name), (Number), (Email), (Comment)
              </p>
              {getFieldError('lead_notification_sms_en') && (
                <p className="mt-1 text-sm text-red-600">{getFieldError('lead_notification_sms_en')}</p>
              )}
              {changedFields.has('lead_notification_sms_en') && (
                <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
              )}
            </div>

            {/* Lead SMS Template French */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Lead SMS Template (French)
              </label>
              <textarea
                value={formData.lead_notification_sms_fr || ''}
                onChange={(e) => handleInputChange('lead_notification_sms_fr', e.target.value)}
                placeholder="SMS template sent to brokers for new leads (French)"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                  getFieldError('lead_notification_sms_fr') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Available placeholders: (Name), (Number), (Email), (Comment)
              </p>
              {getFieldError('lead_notification_sms_fr') && (
                <p className="mt-1 text-sm text-red-600">{getFieldError('lead_notification_sms_fr')}</p>
              )}
              {changedFields.has('lead_notification_sms_fr') && (
                <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
              )}
            </div>
          </div>

          {/* SMS Actions Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
              ⚡ SMS Actions
            </h3>
            
            {/* Enable SMS Actions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enable SMS Actions
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.sms_actions_enabled === 'true'}
                  onChange={(e) => handleInputChange('sms_actions_enabled', e.target.checked ? 'true' : 'false')}
                  className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Enable SMS-based actions for brokers (e.g., lead creation via SMS commands)
                </span>
              </div>
              {changedFields.has('sms_actions_enabled') && (
                <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
              )}
            </div>

            {/* Lead SMS Command Section */}
            <div className="pl-4 border-l-2 border-blue-200 dark:border-blue-700 space-y-4">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Lead Creation Command</h4>
              
              {/* Enable Lead Command */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enable Lead SMS Command
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.lead_sms_command_enabled === 'true'}
                    onChange={(e) => handleInputChange('lead_sms_command_enabled', e.target.checked ? 'true' : 'false')}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Allow brokers to create leads by texting the default number
                  </span>
                </div>
                {changedFields.has('lead_sms_command_enabled') && (
                  <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                )}
              </div>

              {/* Lead Command Keyword */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Command Keyword
                </label>
                <input
                  type="text"
                  value={formData.lead_sms_command_keyword || ''}
                  onChange={(e) => handleInputChange('lead_sms_command_keyword', e.target.value)}
                  placeholder="lead"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                    getFieldError('lead_sms_command_keyword') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Keyword that brokers use to start lead creation commands (case-insensitive)
                </p>
                {getFieldError('lead_sms_command_keyword') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('lead_sms_command_keyword')}</p>
                )}
                {changedFields.has('lead_sms_command_keyword') && (
                  <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                )}
              </div>

              {/* Help Messages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Help Message (English)
                  </label>
                  <textarea
                    value={formData.lead_sms_help_message_en || ''}
                    onChange={(e) => handleInputChange('lead_sms_help_message_en', e.target.value)}
                    placeholder="Instructions sent when broker texts only the keyword"
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_help_message_en') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {changedFields.has('lead_sms_help_message_en') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Help Message (French)
                  </label>
                  <textarea
                    value={formData.lead_sms_help_message_fr || ''}
                    onChange={(e) => handleInputChange('lead_sms_help_message_fr', e.target.value)}
                    placeholder="Instructions sent when broker texts only the keyword"
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_help_message_fr') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {changedFields.has('lead_sms_help_message_fr') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>
              </div>

              {/* Success Messages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Success Message (English)
                  </label>
                  <textarea
                    value={formData.lead_sms_success_message_en || ''}
                    onChange={(e) => handleInputChange('lead_sms_success_message_en', e.target.value)}
                    placeholder="Message sent when lead is created successfully"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_success_message_en') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Available placeholders: (Name), (Email)
                  </p>
                  {changedFields.has('lead_sms_success_message_en') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Success Message (French)
                  </label>
                  <textarea
                    value={formData.lead_sms_success_message_fr || ''}
                    onChange={(e) => handleInputChange('lead_sms_success_message_fr', e.target.value)}
                    placeholder="Message sent when lead is created successfully"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_success_message_fr') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Available placeholders: (Name), (Email)
                  </p>
                  {changedFields.has('lead_sms_success_message_fr') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>
              </div>

              {/* Error Messages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Message (English)
                  </label>
                  <textarea
                    value={formData.lead_sms_error_message_en || ''}
                    onChange={(e) => handleInputChange('lead_sms_error_message_en', e.target.value)}
                    placeholder="Message sent when lead creation fails"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_error_message_en') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {changedFields.has('lead_sms_error_message_en') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Message (French)
                  </label>
                  <textarea
                    value={formData.lead_sms_error_message_fr || ''}
                    onChange={(e) => handleInputChange('lead_sms_error_message_fr', e.target.value)}
                    placeholder="Message sent when lead creation fails"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                      getFieldError('lead_sms_error_message_fr') ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {changedFields.has('lead_sms_error_message_fr') && (
                    <p className="mt-1 text-xs text-orange-600 font-medium">• Modified</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={saving || changedFields.size === 0}
              className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                changedFields.size === 0
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800 focus:ring-gray-900'
              } ${saving ? 'bg-gray-400 cursor-not-allowed' : ''}`}
            >
              {saving 
                ? '💾 Saving...' 
                : changedFields.size === 0 
                  ? '💾 No Changes to Save'
                  : `💾 Save ${changedFields.size} Change${changedFields.size > 1 ? 's' : ''}`
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TelnyxSettings 